<template>
  <InputWrapper
    v-model="model"
    className="input-row--number"
    :name="name"
    :error="error"
    :label="label"
    :disabled="disabled ?? false"
    :hasIconLeft="true"
    :hasIconRight="hasIconRight"
    :placeholder="placeholder || (mask ?? undefined)"
  >
    <template #icon>
      <slot name="icon">
        <Button
          @click="stepDown"
          severity="secondary"
          outlined
          class="input-number--button"
          aria-label="Decrease number by step"
        >
          <AccordionMinus />
        </Button>
      </slot>
    </template>

    <template #input>
      <input
        :id="`${name}`"
        :name="`${name}`"
        :min="min"
        :step="step ?? 1"
        ref="input-text"
        type="string"
        v-model="internalValue"
        v-maska="mask ?? undefined"
        data-maska-number
        data-maska-number-fraction="2"
        data-maska-number-unsigned
        :placeholder="mask ?? undefined"
        class="form-input form-input--text"
        :disabled="disabled ?? undefined"
        @blur="onBlur"
        @keyup.enter="onEnter"
        @keydown="onInputKeyDown"
      />
    </template>

    <template #icon-right>
      <slot name="icon-right">
        <Button
          @click="stepUp"
          severity="secondary"
          outlined
          class="input-number--button"
          aria-label="Increase number by step"
        >
          <AccordionPlus />
        </Button>
      </slot>
    </template>
  </InputWrapper>
</template>

<script setup lang="ts">
import { ref, useTemplateRef, watch } from 'vue';
import { vMaska } from 'maska/vue';
import { Button } from 'primevue';

import InputWrapper from './InputWrapper.vue';

import AccordionPlus from '@/icons/AccordionPlus.svg?component';
import AccordionMinus from '@/icons/AccordionMinus.svg?component';

const model = defineModel<number>();
const emit = defineEmits(['onEnter', 'onBlur']);

const inputText = useTemplateRef('input-text');

const internalValue = ref('');

const props = withDefaults(
  defineProps<{
    label?: string;
    labelPos?: string;
    disabled?: boolean | null;
    id?: string;
    mask?: string;
    name?: string;
    error?: string;
    hasIconLeft?: boolean;
    hasIconRight?: boolean;
    placeholder?: string;
    min?: number;
    max?: number;
    step?: number;
  }>(),
  {
    label: '',
    labelPos: 'top',
    disabled: null,
    id: undefined,
    name: '',
    error: '',
    hasIconLeft: false,
    hasIconRight: false,
    placeholder: '',
    min: 0,
    step: 1,
  },
);

const stepUp = () => {
  const internalNumber = parseFloat(internalValue.value);
  if (props.max) {
    if (parseFloat((internalNumber + props.step).toFixed(2)) <= props.max) {
      model.value = parseFloat((internalNumber + props.step).toFixed(2));
    }
  } else {
    model.value = parseFloat((internalNumber + props.step).toFixed(2));
  }
};

const stepDown = () => {
  const internalNumber = parseFloat(internalValue.value);
  if (parseFloat((internalNumber - props.step).toFixed(2)) >= props.min) {
    model.value = parseFloat((internalNumber - props.step).toFixed(2));
  }
};

const onInputKeyDown = (event: KeyboardEvent) => {
  const code = event.code || event.key;
  if (model.value) {
    if (code === 'ArrowUp') {
      event.preventDefault();
      stepUp();
    } else if (code === 'ArrowDown') {
      event.preventDefault();
      stepDown();
    }
  }
};

watch(
  model,
  () => {
    internalValue.value = `${model.value?.toFixed(2)}`;

    // Shouldn't become an empty string but in case it does.
    if (internalValue.value === '') {
      internalValue.value = `${props.min.toFixed(2)}`;
    }
  },
  { immediate: true },
);

const updateModel = () => {
  if (internalValue.value) {
    model.value = parseFloat(internalValue.value);
  } else {
    model.value = props.min;
  }
};

const onBlur = (e: FocusEvent) => {
  updateModel();
  emit('onBlur', e);
};

const onEnter = (e: KeyboardEvent) => {
  updateModel();
  emit('onEnter', e);
};

defineExpose({
  inputText,
});
</script>
